import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { supabase, apiFetch } from "@/lib/supabase";
import { useAuth } from "@/context/AuthContext";
import { logActivity } from "@/lib/activity";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  ArrowLeft, Edit, Calendar, ListChecks, StickyNote, UploadCloud, Download, Trash2, Plus,
  ClipboardList, FileText, Link2, Copy, X as XIcon, Archive, Info, Activity, MessageCircle,
  DollarSign, Users, ShieldCheck, Clipboard, FileDown, Sparkles, RotateCcw,
} from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { StatusBadge, formatDate, formatDateTime } from "@/lib/ui";
import { fetchAndSave } from "@/lib/downloads";

import TimelineTab from "@/components/client-tabs/TimelineTab";
import VisitsTab from "@/components/client-tabs/VisitsTab";
import CommunicationsTab from "@/components/client-tabs/CommunicationsTab";
import CareTeamTab from "@/components/client-tabs/CareTeamTab";
import FormsTab from "@/components/client-tabs/FormsTab";
import BillingTab from "@/components/client-tabs/BillingTab";
import AuditTab from "@/components/client-tabs/AuditTab";
import { openExternal } from "@/lib/external";

const TABS = [
  { key: "overview", label: "Overview", icon: Info },
  { key: "timeline", label: "Timeline", icon: Activity },
  { key: "appointments", label: "Appointments", icon: Calendar },
  { key: "bookings", label: "Bookings", icon: Calendar },
  { key: "qa", label: "Q&A", icon: ClipboardList },
  { key: "visits", label: "Visits", icon: Activity },
  { key: "notes", label: "Notes", icon: StickyNote },
  { key: "forms", label: "Forms", icon: ClipboardList },
  { key: "documents", label: "Documents", icon: FileText },
  { key: "tasks", label: "Tasks", icon: ListChecks },
  { key: "communications", label: "Communications", icon: MessageCircle },
  { key: "billing", label: "Billing", icon: DollarSign },
  { key: "care_team", label: "Care Team", icon: Users },
  { key: "intake", label: "Client Intake", icon: Clipboard },
  { key: "audit", label: "Audit", icon: ShieldCheck },
];

export default function ClientProfile() {
  const { id } = useParams();
  const { currentOrgId, user, currentRole } = useAuth();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [tab, setTab] = useState(() => {
    if (typeof window !== "undefined") {
      const p = new URLSearchParams(window.location.search).get("tab");
      if (p && ["overview","timeline","appointments","bookings","qa","visits","notes","forms","documents","tasks","communications","billing","care_team","intake","audit"].includes(p)) return p;
    }
    return "overview";
  });
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiData, setAiData] = useState(null);
  const [aiError, setAiError] = useState(null);
  const generateAiSummary = async () => {
    if (aiLoading) return;   // duplicate-request guard
    setAiLoading(true); setAiError(null); setAiData(null);
    // Hard 60 s timeout so an iOS WKWebView network stall surfaces the
    // friendly error state instead of leaving the drawer spinning forever.
    const ac = new AbortController();
    const timer = setTimeout(() => ac.abort(), 60000);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) { setAiError("Please sign in again."); return; }
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/clients/${id}/ai-summary`,
        { method: "POST",
          headers: { "Authorization": `Bearer ${session.access_token}` },
          signal: ac.signal });
      if (!res.ok) {
        setAiError("We couldn't generate the summary. Please try again.");
        return;
      }
      const j = await res.json();
      setAiData(j);
    } catch (e) {
      setAiError("We couldn't generate the summary. Please try again.");
    } finally {
      clearTimeout(timer);
      setAiLoading(false);
    }
  };
  useEffect(() => {
    // Auto-generate the first time the drawer is opened; do NOT re-run on
    // page refresh or on close/reopen.
    if (aiOpen && !aiData && !aiLoading && !aiError) generateAiSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aiOpen]);
  const copyAiSummary = async () => {
    if (!aiData?.sections) return;
    const s = aiData.sections;
    const txt = [
      "Client Overview\n" + (s.client_overview || "—"),
      "Current Priorities\n" + (s.current_priorities || "—"),
      "Open Items\n" + (s.open_items || "—"),
      "Suggested Next Steps\n" + (s.suggested_next_steps || "—"),
      "\nAI-generated summary. Review the information before relying on it or taking action.",
    ].join("\n\n");
    try { await navigator.clipboard.writeText(txt); toast.success("Summary copied"); }
    catch { toast.error("Could not copy"); }
  };
  const [editForm, setEditForm] = useState(null);
  const [portalOpen, setPortalOpen] = useState(false);
  const [portalToken, setPortalToken] = useState(null);
  const [portalLoading, setPortalLoading] = useState(false);
  const isReadOnly = currentRole === "read_only";

  const [appointments, setAppointments] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [notes, setNotes] = useState([]);
  const [intake, setIntake] = useState(null);
  const [visitCount, setVisitCount] = useState(0);
  const [teamCount, setTeamCount] = useState(0);
  const [invoices, setInvoices] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [formAssignments, setFormAssignments] = useState([]);
  const [newNote, setNewNote] = useState("");
  const [savingNote, setSavingNote] = useState(false);

  const load = async () => {
    setLoading(true);
    const { data } = await supabase.from("clients").select("*").eq("id", id).limit(1);
    setClient(data?.[0] || null);
    setLoading(false); // render main profile immediately; secondary counts populate below
    const [a, t, d, n, i, v, ct, bk, inv, ctr, fa] = await Promise.allSettled([
      supabase.from("appointments").select("*").eq("client_id", id).order("starts_at", { ascending: false }),
      supabase.from("tasks").select("*").eq("client_id", id).order("created_at", { ascending: false }),
      supabase.from("documents").select("*,profiles:uploaded_by(full_name)").eq("client_id", id).order("created_at", { ascending: false }),
      supabase.from("notes").select("*,profiles:created_by(full_name)").eq("client_id", id).order("created_at", { ascending: false }),
      supabase.from("intake_records").select("*").eq("client_id", id).limit(1),
      supabase.from("visits").select("id", { count: "exact", head: true }).eq("client_id", id),
      supabase.from("care_team").select("id", { count: "exact", head: true }).eq("client_id", id),
      supabase.from("bookings").select("id,starts_at,ends_at,duration_minutes,status,location_type,meeting_url,appointment_type_snapshot,client_answers,client_notes,manage_token,cancelled_at,cancellation_reason").eq("client_id", id).order("starts_at", { ascending: false }),
      supabase.from("invoices").select("id,invoice_number,status,total_cents,currency,due_date,issued_at").eq("client_id", id).order("issued_at", { ascending: false }),
      supabase.from("service_contracts").select("id,contract_name,contract_number,status,effective_date,signed_at").eq("client_id", id).order("created_at", { ascending: false }),
      supabase.from("form_assignments").select("id,status,created_at,submitted_at,form_templates(id,name)").eq("client_id", id).order("created_at", { ascending: false }),
    ]);
    setAppointments(a.status === "fulfilled" ? (a.value.data || []) : []);
    setTasks(t.status === "fulfilled" ? (t.value.data || []) : []);
    setDocuments(d.status === "fulfilled" ? (d.value.data || []) : []);
    setNotes(n.status === "fulfilled" ? (n.value.data || []) : []);
    setIntake(i.status === "fulfilled" ? (i.value.data?.[0] || null) : null);
    setVisitCount(v.status === "fulfilled" ? (v.value.count || 0) : 0);
    setTeamCount(ct.status === "fulfilled" ? (ct.value.count || 0) : 0);
    setBookings(bk.status === "fulfilled" ? (bk.value.data || []) : []);
    setInvoices(inv.status === "fulfilled" ? (inv.value.data || []) : []);
    setContracts(ctr.status === "fulfilled" ? (ctr.value.data || []) : []);
    setFormAssignments(fa.status === "fulfilled" ? (fa.value.data || []) : []);
  };

  useEffect(() => { if (currentOrgId) load(); /* eslint-disable-next-line */ }, [id, currentOrgId]);

  if (loading) return <div className="text-center py-16 text-[#57534E]">Loading…</div>;
  if (!client) return <div className="text-center py-16 text-[#57534E]">Client not found.</div>;

  const openEdit = () => {
    setEditForm({
      first_name: client.first_name || "", last_name: client.last_name || "",
      preferred_name: client.preferred_name || "", date_of_birth: client.date_of_birth || "",
      phone: client.phone || "", email: client.email || "",
      street_address: client.street_address || "", city: client.city || "",
      state: client.state || "", zip_code: client.zip_code || "",
      emergency_contact_name: client.emergency_contact_name || "",
      emergency_contact_phone: client.emergency_contact_phone || "",
      insurance_info: client.insurance_info || "",
      referral_source: client.referral_source || "",
      intake_date: client.intake_date || "",
      status: client.status || "active", notes: client.notes || "",
    });
    setEditOpen(true);
  };

  const saveEdit = async () => {
    if (!editForm.first_name.trim() || !editForm.last_name.trim())
      return toast.error("First name and last name are required");
    const payload = { ...editForm };
    Object.keys(payload).forEach((k) => { if (payload[k] === "") payload[k] = null; });
    const { error } = await supabase.from("clients").update(payload).eq("id", id);
    if (error) return toast.error(error.message);
    toast.success("Client updated");
    setEditOpen(false);
    load();
    logActivity({ organization_id: currentOrgId, actor_user_id: user.id, client_id: id,
      action: "client.updated", summary: `updated ${payload.first_name} ${payload.last_name}`,
      entity_type: "clients", entity_id: id });
  };

  const startIntake = async () => {
    const { error } = await supabase.from("intake_records").insert({
      organization_id: currentOrgId, client_id: id, status: "in_progress",
      started_at: new Date().toISOString(), created_by: user.id, assigned_to: user.id,
    });
    if (error) return toast.error(error.message);
    toast.success("Intake started");
    load();
    logActivity({ organization_id: currentOrgId, actor_user_id: user.id, client_id: id,
      action: "intake.started", summary: `started intake for ${client.first_name} ${client.last_name}`,
      entity_type: "intake_records" });
  };

  const addNote = async () => {
    if (!newNote.trim()) return;
    setSavingNote(true);
    const { error } = await supabase.from("notes").insert({
      organization_id: currentOrgId, client_id: id, body: newNote.trim(), created_by: user.id,
    });
    setSavingNote(false);
    if (error) return toast.error(error.message);
    setNewNote("");
    load();
    logActivity({ organization_id: currentOrgId, actor_user_id: user.id, client_id: id,
      action: "note.created", summary: `added a note to ${client.first_name} ${client.last_name}`,
      entity_type: "notes" });
  };

  const deleteNote = async (noteId) => {
    if (!window.confirm("Delete this note?")) return;
    const { error } = await supabase.from("notes").delete().eq("id", noteId);
    if (error) return toast.error(error.message);
    toast.success("Note deleted");
    load();
  };

  const uploadDoc = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const docType = window.prompt("Document type (intake_form, identification, insurance, referral, assessment, treatment_plan, other):", "other") || "other";
    const path = `orgs/${currentOrgId}/clients/${id}/${crypto.randomUUID()}-${file.name}`;
    const { error: upErr } = await supabase.storage.from("documents").upload(path, file, { contentType: file.type });
    if (upErr) return toast.error(upErr.message);
    const { error: dbErr } = await supabase.from("documents").insert({
      organization_id: currentOrgId, client_id: id, name: file.name,
      document_type: docType, storage_path: path, mime_type: file.type, size_bytes: file.size,
      uploaded_by: user.id,
    });
    if (dbErr) return toast.error(dbErr.message);
    toast.success("Document uploaded");
    e.target.value = "";
    load();
    logActivity({ organization_id: currentOrgId, actor_user_id: user.id, client_id: id,
      action: "document.uploaded", summary: `uploaded ${file.name}`,
      entity_type: "documents" });
  };

  const downloadDoc = async (docId) => {
    try {
      const r = await apiFetch(`/documents/${docId}/download`);
      openExternal(r.url);
    } catch (e) { toast.error(e.message); }
  };

  const deleteDoc = async (doc) => {
    if (!window.confirm(`Delete "${doc.name}"?`)) return;
    await supabase.storage.from("documents").remove([doc.storage_path]);
    await supabase.from("documents").delete().eq("id", doc.id);
    toast.success("Document deleted");
    load();
  };

  const openPortal = async () => {
    setPortalOpen(true);
    setPortalLoading(true);
    try {
      const r = await apiFetch(`/clients/${id}/portal-token`, {
        method: "POST", body: JSON.stringify({ organization_id: currentOrgId }),
      });
      setPortalToken(r.token);
    } catch (e) { toast.error(e.message); } finally { setPortalLoading(false); }
  };

  // Client relationship state is split across TWO independent columns:
  //   • status    — 'active' | 'pending' | 'inactive'  (relationship state; client still visible in normal lists)
  //   • archived  — boolean                            (removed from day-to-day lists; retained for history)
  // Each action below changes exactly ONE column so the two concepts cannot get conflated.
  //
  // .select() is CRITICAL — Supabase-JS `.update({}).eq()` returns null on
  // both success AND silent-RLS-block. Only with `.select()` do we get the
  // affected rows back so we can detect a permission-denied silent block
  // (data.length === 0 with no error). We refetch the client after every
  // successful change so the UI never diverges from the server.
  const _updateClient = async (patch) => {
    const { data, error } = await supabase.from("clients")
      .update(patch).eq("id", id).select().single();
    if (error) { toast.error(error.message); return null; }
    if (!data) { toast.error("Update was blocked. You may not have permission to change this client."); return null; }
    return data;
  };
  const markInactive = async () => {
    const d = await _updateClient({ status: "inactive" });
    if (!d) return;
    setClient(d);
    toast.success("Marked inactive");
    logActivity({ organization_id: currentOrgId, actor_user_id: user.id, client_id: id,
      action: "client.status.inactive", summary: `marked ${client.first_name} ${client.last_name} inactive`,
      entity_type: "clients", entity_id: id });
  };
  const reactivateClient = async () => {
    const d = await _updateClient({ status: "active" });
    if (!d) return;
    setClient(d);
    toast.success("Reactivated");
    logActivity({ organization_id: currentOrgId, actor_user_id: user.id, client_id: id,
      action: "client.status.active", summary: `reactivated ${client.first_name} ${client.last_name}`,
      entity_type: "clients", entity_id: id });
  };
  const archiveClient = async () => {
    if (!window.confirm(`Archive ${client.first_name} ${client.last_name}? They'll be hidden from the normal client lists but all data (tasks, documents, bookings, invoices, forms, contracts, history) is preserved.`)) return;
    const d = await _updateClient({ archived: true });
    if (!d) return;
    toast.success("Client archived");
    logActivity({ organization_id: currentOrgId, actor_user_id: user.id, client_id: id,
      action: "client.archived", summary: `archived ${client.first_name} ${client.last_name}`,
      entity_type: "clients", entity_id: id });
    navigate("/app/clients");
  };
  const restoreClient = async () => {
    const d = await _updateClient({ archived: false });
    if (!d) return;
    setClient(d);
    toast.success("Client restored");
    logActivity({ organization_id: currentOrgId, actor_user_id: user.id, client_id: id,
      action: "client.restored", summary: `restored ${client.first_name} ${client.last_name}`,
      entity_type: "clients", entity_id: id });
  };

  const revokePortal = async () => {
    if (!window.confirm("Revoke this link? Existing link will stop working.")) return;
    try {
      await apiFetch(`/clients/${id}/portal-token`, { method: "DELETE" });
      setPortalToken(null);
      toast.success("Portal link revoked");
    } catch (e) { toast.error(e.message); }
  };

  const portalUrl = portalToken ? `${window.location.origin}/portal/${portalToken}` : "";

  return (
    <div className="space-y-6 animate-fade-in-up" data-testid="client-profile-page">
      <Link to="/app/clients" className="inline-flex items-center gap-1.5 text-sm text-[#57534E] hover:text-[#E8712C]" data-testid="back-to-clients">
        <ArrowLeft size={14} /> Back to clients
      </Link>

      <div className="bg-white rounded-2xl border border-[#E8E2D9] p-6 flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-2xl bg-[#FDE9D8] flex items-center justify-center text-[#E8712C] font-bold text-xl">
            {(client.first_name?.[0] || "?").toUpperCase()}{(client.last_name?.[0] || "").toUpperCase()}
          </div>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="font-display font-bold text-2xl tracking-tight" data-testid="client-name">
                {client.first_name} {client.last_name}
              </h1>
              <StatusBadge value={client.status} testid="client-status" />
            </div>
            <div className="text-sm text-[#57534E] mt-1">
              {client.email && <span className="mr-3">{client.email}</span>}
              {client.phone && <span>{client.phone}</span>}
              {!client.email && !client.phone && <span className="text-[#A8A29E]">No contact info</span>}
            </div>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            data-testid="client-ai-summary-btn"
            onClick={() => setAiOpen(true)}
            className="rounded-full bg-[#E8712C] hover:bg-[#C55A1F]"
          ><Sparkles size={14} className="mr-1.5" /> Generate AI Summary</Button>
          <Button
            data-testid="client-download-pdf-btn"
            variant="outline"
            className="rounded-full"
            onClick={async () => {
              try {
                const { data: { session } } = await supabase.auth.getSession();
                if (!session) return toast.error("Please sign in again");
                await fetchAndSave(
                  `${process.env.REACT_APP_BACKEND_URL}/api/clients/${client.id}/pdf`,
                  { token: session.access_token,
                    filename: `${(client.first_name || "client")}-${(client.last_name || "")}-summary.pdf`.replace(/\s+/g, "-").toLowerCase() }
                );
              } catch (e) { toast.error(e.message || "PDF download failed"); }
            }}
          ><FileDown size={14} className="mr-1.5" /> Download PDF</Button>
          {!isReadOnly && <Button data-testid="edit-client-btn" onClick={openEdit} variant="outline" className="rounded-full"><Edit size={14} className="mr-1.5" /> Edit</Button>}
          {/* Status actions — placed immediately after Edit so they're always visible even when the row wraps. */}
          {currentRole === "org_admin" && !client.archived && client.status !== "inactive" && (
            <Button data-testid="mark-inactive-btn" onClick={markInactive} variant="outline" className="rounded-full">Mark inactive</Button>
          )}
          {currentRole === "org_admin" && !client.archived && client.status === "inactive" && (
            <Button data-testid="reactivate-btn" onClick={reactivateClient} variant="outline" className="rounded-full text-emerald-700 border-emerald-200">Reactivate</Button>
          )}
          {currentRole === "org_admin" && !client.archived && (
            <Button data-testid="archive-client-btn" onClick={archiveClient} variant="outline" className="rounded-full text-red-600 border-red-200"><Archive size={14} className="mr-1.5" /> Archive</Button>
          )}
          {currentRole === "org_admin" && client.archived && (
            <Button data-testid="restore-client-btn" onClick={restoreClient} variant="outline" className="rounded-full text-emerald-700 border-emerald-200"><Archive size={14} className="mr-1.5" /> Restore</Button>
          )}
          {currentRole === "org_admin" && (
            <Button data-testid="toggle-test-client-btn"
                    onClick={async () => {
                      try {
                        await apiFetch(`/scheduling/clients/${id}/test-flag`, {
                          method: "PATCH",
                          body: JSON.stringify({ organization_id: currentOrgId, is_test: !client.is_test }),
                        });
                        setClient({ ...client, is_test: !client.is_test });
                        toast.success(client.is_test ? "Removed test flag" : "Marked as test client");
                      } catch (e) { toast.error(e.message); }
                    }}
                    variant="outline" className={`rounded-full ${client.is_test ? "border-amber-400 text-amber-700" : ""}`}>
              {client.is_test ? "✓ Test client" : "Mark as test"}
            </Button>
          )}
          <Button data-testid="add-appt-btn" onClick={() => navigate(`/app/calendar?new=1&client_id=${id}`)} variant="outline" className="rounded-full"><Calendar size={14} className="mr-1.5" /> Add appointment</Button>
          <Button data-testid="add-task-btn" onClick={() => navigate(`/app/tasks?new=1&client_id=${id}`)} variant="outline" className="rounded-full"><ListChecks size={14} className="mr-1.5" /> Add task</Button>
          <Button data-testid="upload-doc-btn" onClick={() => document.getElementById("doc-file-input")?.click()} variant="outline" className="rounded-full"><UploadCloud size={14} className="mr-1.5" /> Upload document</Button>
          {!isReadOnly && <Button data-testid="portal-link-btn" onClick={openPortal} variant="outline" className="rounded-full"><Link2 size={14} className="mr-1.5" /> Client portal link</Button>}
          <input id="doc-file-input" type="file" hidden onChange={uploadDoc} data-testid="doc-file-input" />
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-[#E8E2D9] overflow-x-auto sticky top-0 bg-[#FAF8F5] z-10">
        {TABS.map((t) => {
          const Icon = t.icon;
          const badge = t.key === "appointments" ? appointments.length
            : t.key === "bookings" ? bookings.length
            : t.key === "qa" ? bookings.filter((b) => b.client_answers && Object.keys(b.client_answers || {}).length > 0).length
            : t.key === "tasks" ? tasks.filter((x) => x.status !== "completed").length
            : t.key === "documents" ? documents.length
            : t.key === "notes" ? notes.length
            : t.key === "visits" ? visitCount
            : t.key === "care_team" ? teamCount
            : null;
          return (
            <button key={t.key} data-testid={`tab-${t.key}`} onClick={() => setTab(t.key)}
              className={`px-3 py-3 text-sm font-medium whitespace-nowrap border-b-2 -mb-px transition-colors inline-flex items-center gap-1.5 ${
                tab === t.key ? "border-[#E8712C] text-[#E8712C]" : "border-transparent text-[#57534E] hover:text-[#1C1917]"
              }`}>
              <Icon size={14} strokeWidth={1.75} />
              {t.label}
              {badge != null && badge > 0 && (
                <span className={`ml-0.5 text-[10px] px-1.5 py-0.5 rounded-full ${tab === t.key ? "bg-[#FDE9D8] text-[#E8712C]" : "bg-[#F5EDE4] text-[#57534E]"}`}>{badge}</span>
              )}
            </button>
          );
        })}
      </div>

      {tab === "overview" && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-testid="overview-tab">
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-white rounded-2xl border border-[#E8E2D9] p-6">
              <h3 className="font-display font-semibold text-sm text-[#57534E] uppercase tracking-widest mb-4">Basic information</h3>
              <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                <Field label="First name" value={client.first_name} />
                <Field label="Last name" value={client.last_name} />
                <Field label="Preferred name" value={client.preferred_name} />
                <Field label="Date of birth" value={client.date_of_birth ? formatDate(client.date_of_birth) : null} />
                <Field label="Phone" value={client.phone} />
                <Field label="Email" value={client.email} />
                <Field label="Address" value={[client.street_address, client.city, client.state, client.zip_code].filter(Boolean).join(", ")} full />
                <Field label="Emergency contact" value={client.emergency_contact_name} />
                <Field label="Emergency phone" value={client.emergency_contact_phone} />
                <Field label="Referral source" value={client.referral_source} />
                <Field label="Intake date" value={client.intake_date ? formatDate(client.intake_date) : null} />
                <Field label="Insurance" value={client.insurance_info} full />
                <Field label="Notes" value={client.notes} full />
              </dl>
            </div>
          </div>
          <div className="space-y-4">
            <OverviewSummary
              client={client}
              appointments={appointments}
              bookings={bookings}
              tasks={tasks}
              invoices={invoices}
              contracts={contracts}
              formAssignments={formAssignments}
              onNav={(t) => setTab(t)}
              canEdit={!isReadOnly}
            />
          </div>
        </div>
      )}

      {tab === "timeline" && <TimelineTab clientId={id} />}

      {tab === "appointments" && (
        <ListPanel
          testid="appts-tab"
          empty={{
            icon: Calendar,
            title: "No appointments yet",
            desc: "Schedule the first appointment for this client.",
            action: !isReadOnly && (
              <Button
                onClick={() => navigate(`/app/calendar?new=1&client_id=${id}`)}
                data-testid="empty-cta-schedule-appt"
                className="rounded-full bg-[#E8712C] hover:bg-[#C55A1F]"
              >
                <Plus size={14} className="mr-1.5" /> Schedule appointment
              </Button>
            ),
          }}
        >
          {appointments.map((a) => (
            <button
              key={a.id}
              onClick={() => navigate(`/app/calendar?id=${a.id}`)}
              data-testid={`profile-appt-${a.id}`}
              className="w-full text-left px-6 py-3 flex items-center gap-4 border-b border-[#E8E2D9] last:border-0 hover:bg-[#FAF8F5] transition-colors"
            >
              <div className="w-24 text-sm font-medium text-[#E8712C] flex-shrink-0">{formatDateTime(a.starts_at)}</div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium">{a.title}</div>
                <div className="text-xs text-[#57534E]">{a.appointment_type}</div>
              </div>
              <StatusBadge value={a.status} />
            </button>
          ))}
        </ListPanel>
      )}

      {tab === "bookings" && (
        <div data-testid="bookings-tab" className="bg-white rounded-2xl border border-[#E8E2D9]">
          {bookings.length === 0 ? (
            <div className="text-center py-10">
              <Calendar size={22} className="mx-auto text-[#A8A29E] mb-2" strokeWidth={1.5} />
              <p className="text-sm text-[#57534E]">No public bookings yet.</p>
            </div>
          ) : (
            <div className="divide-y divide-[#E8E2D9]">
              {bookings.map((b) => {
                const snap = b.appointment_type_snapshot || {};
                return (
                  <div key={b.id} className="hover:bg-[#FAF8F5] transition-colors" data-testid={`profile-booking-${b.id}`}>
                    <button
                      onClick={() => navigate(`/app/scheduling?id=${b.id}`)}
                      className="w-full text-left px-6 py-3 flex items-center gap-4 flex-wrap"
                    >
                      <div className="w-32 text-sm font-medium text-[#E8712C] flex-shrink-0">{formatDateTime(b.starts_at)}</div>
                      <div className="flex-1 min-w-[180px]">
                        <div className="text-sm font-medium">{snap.name || "Booking"}</div>
                        <div className="text-xs text-[#57534E]">
                          {b.duration_minutes} min
                          {snap.location_type ? ` · ${snap.location_type.replace(/_/g, " ")}` : ""}
                        </div>
                      </div>
                      {b.meeting_url && (
                        <a href={b.meeting_url} target="_blank" rel="noreferrer"
                           onClick={(e) => e.stopPropagation()}
                           className="text-xs text-[#E8712C] hover:underline">Join →</a>
                      )}
                      <StatusBadge value={b.status} />
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {tab === "qa" && (
        <div data-testid="qa-tab" className="space-y-3">
          {bookings.filter((b) => b.client_answers && Object.keys(b.client_answers || {}).length > 0).length === 0 ? (
            <div className="text-center py-10 bg-white rounded-2xl border border-[#E8E2D9]">
              <ClipboardList size={22} className="mx-auto text-[#A8A29E] mb-2" strokeWidth={1.5} />
              <p className="text-sm text-[#57534E]">No booking questions answered yet.</p>
            </div>
          ) : (
            bookings.filter((b) => b.client_answers && Object.keys(b.client_answers || {}).length > 0).map((b) => {
              const snap = b.appointment_type_snapshot || {};
              const questions = snap.custom_questions || [];
              const answers = b.client_answers || {};
              const qByKey = Object.fromEntries((questions || []).map((q) => [q.key || q.label, q]));
              return (
                <div key={b.id} className="bg-white rounded-2xl border border-[#E8E2D9] p-4"
                     data-testid={`qa-booking-${b.id}`}>
                  <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
                    <div>
                      <div className="text-sm font-medium">{snap.name || "Booking"} — {formatDateTime(b.starts_at)}</div>
                      <div className="text-xs text-[#A8A29E]">{b.status} · {b.duration_minutes} min</div>
                    </div>
                    <StatusBadge value={b.status} />
                  </div>
                  <dl className="space-y-2">
                    {Object.entries(answers).map(([key, val]) => {
                      const q = qByKey[key];
                      const label = q?.label || key.replace(/_/g, " ");
                      const display = Array.isArray(val) ? val.join(", ") : String(val);
                      return (
                        <div key={key}>
                          <dt className="text-xs font-medium text-[#57534E]">{label}</dt>
                          <dd className="text-sm mt-0.5 whitespace-pre-wrap">{display || <span className="text-[#A8A29E]">—</span>}</dd>
                        </div>
                      );
                    })}
                  </dl>
                </div>
              );
            })
          )}
        </div>
      )}

      {tab === "visits" && <VisitsTab clientId={id} isReadOnly={isReadOnly} onChange={load} />}

      {tab === "notes" && (
        <div data-testid="notes-tab" className="space-y-4">
          {!isReadOnly && (
            <div className="bg-white rounded-2xl border border-[#E8E2D9] p-4">
              <Textarea data-testid="new-note-body" rows={3} value={newNote} onChange={(e) => setNewNote(e.target.value)} placeholder="Add a note about this client…" />
              <div className="mt-3 flex justify-end">
                <Button data-testid="save-note-btn" onClick={addNote} disabled={savingNote || !newNote.trim()} className="rounded-full bg-[#E8712C] hover:bg-[#C55A1F]"><Plus size={14} className="mr-1" /> Add note</Button>
              </div>
            </div>
          )}
          {notes.length === 0 ? (
            <div className="text-center py-10 bg-white rounded-2xl border border-[#E8E2D9]">
              <StickyNote size={22} className="mx-auto text-[#A8A29E] mb-2" strokeWidth={1.5} />
              <p className="text-sm text-[#57534E]">No notes yet.</p>
            </div>
          ) : (
            <div className="bg-white rounded-2xl border border-[#E8E2D9] divide-y divide-[#E8E2D9]">
              {notes.map((n) => (
                <div key={n.id} className="p-5" data-testid={`note-${n.id}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-xs text-[#57534E]"><b className="font-medium text-[#1C1917]">{n.profiles?.full_name || "—"}</b> · {formatDateTime(n.created_at)}</div>
                    {(n.created_by === user.id || currentRole === "org_admin") && (
                      <Button data-testid={`del-note-${n.id}`} size="sm" variant="ghost" onClick={() => deleteNote(n.id)} className="text-red-600"><Trash2 size={13} /></Button>
                    )}
                  </div>
                  <div className="text-sm text-[#1C1917] whitespace-pre-wrap">{n.body}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === "forms" && (
        <div className="space-y-4">
          <div className="border-l-2 border-[#E8712C] pl-3">
            <p className="text-xs text-[#57534E]">Assign forms to this client, complete forms on their behalf, and review submitted responses.</p>
          </div>
          <FormsTab clientId={id} client={client} isReadOnly={isReadOnly} onChange={load} />
        </div>
      )}

      {tab === "documents" && (
        <ListPanel
          testid="docs-tab"
          empty={{
            icon: FileText,
            title: "No documents yet",
            desc: "Upload the first document for this client.",
            action: !isReadOnly && (
              <Button
                onClick={() => document.getElementById("doc-file-input")?.click()}
                data-testid="empty-cta-upload-doc"
                className="rounded-full bg-[#E8712C] hover:bg-[#C55A1F]"
              >
                <UploadCloud size={14} className="mr-1.5" /> Upload document
              </Button>
            ),
          }}
        >
          {documents.map((d) => (
            <div key={d.id} className="px-6 py-3 flex items-center gap-3 border-b border-[#E8E2D9] last:border-0 hover:bg-[#FAF8F5] transition-colors">
              <FileText size={18} className="text-[#E8712C] flex-shrink-0" strokeWidth={1.5} />
              <button
                onClick={() => downloadDoc(d.id)}
                data-testid={`profile-doc-${d.id}`}
                className="flex-1 min-w-0 text-left"
                title="Download / open"
              >
                <div className="text-sm font-medium truncate hover:text-[#E8712C]">{d.name}</div>
                <div className="text-xs text-[#57534E]">
                  <span className="capitalize">{d.document_type.replace(/_/g, " ")}</span> · {formatDate(d.created_at)} · {d.profiles?.full_name || "—"}
                </div>
              </button>
              <Button data-testid={`download-${d.id}`} size="sm" variant="ghost" onClick={() => downloadDoc(d.id)}><Download size={14} /></Button>
              {!isReadOnly && <Button data-testid={`del-doc-${d.id}`} size="sm" variant="ghost" onClick={() => deleteDoc(d)} className="text-red-600"><Trash2 size={14} /></Button>}
            </div>
          ))}
        </ListPanel>
      )}

      {tab === "tasks" && (
        <ListPanel
          testid="tasks-tab"
          empty={{
            icon: ListChecks,
            title: "No tasks yet",
            desc: "Assign a task to keep work moving.",
            action: !isReadOnly && (
              <Button
                onClick={() => navigate(`/app/tasks?new=1&client_id=${id}`)}
                data-testid="empty-cta-new-task"
                className="rounded-full bg-[#E8712C] hover:bg-[#C55A1F]"
              >
                <Plus size={14} className="mr-1.5" /> New task
              </Button>
            ),
          }}
        >
          {tasks.map((t) => (
            <button
              key={t.id}
              onClick={() => navigate(`/app/tasks?id=${t.id}`)}
              data-testid={`profile-task-${t.id}`}
              className="w-full text-left px-6 py-3 border-b border-[#E8E2D9] last:border-0 hover:bg-[#FAF8F5] transition-colors"
            >
              <div className="flex items-center justify-between gap-3">
                <div className="text-sm font-medium">{t.title}</div>
                <div className="flex items-center gap-2">
                  <StatusBadge value={t.priority} /><StatusBadge value={t.status} />
                </div>
              </div>
              <div className="text-xs text-[#57534E] mt-1">{t.due_date ? `Due ${formatDate(t.due_date)}` : "No due date"}</div>
            </button>
          ))}
        </ListPanel>
      )}

      {tab === "communications" && <CommunicationsTab clientId={id} isReadOnly={isReadOnly} onChange={load} />}

      {tab === "billing" && <BillingTab clientId={id} isReadOnly={isReadOnly} onChange={load} />}

      {tab === "care_team" && <CareTeamTab clientId={id} isReadOnly={isReadOnly} onChange={load} />}

      {tab === "intake" && (
        <div data-testid="intake-tab" className="space-y-4">
          <div className="border-l-2 border-[#E8712C] pl-3">
            <p className="text-xs text-[#57534E]">Structured client intake covering contact, project, service, consent, and signature details. This is separate from Forms — a distinct record kept for compliance and history.</p>
          </div>
          {intake ? (
            <IntakePanel intake={intake} client={client} onChange={load} />
          ) : (
            <div className="text-center py-12 bg-white rounded-2xl border border-[#E8E2D9]">
              <ClipboardList size={22} className="mx-auto text-[#A8A29E] mb-2" strokeWidth={1.5} />
              <p className="text-sm text-[#57534E] mb-4">No intake started yet.</p>
              <Button data-testid="start-intake-btn" onClick={startIntake} className="rounded-full bg-[#E8712C] hover:bg-[#C55A1F]">Start intake</Button>
            </div>
          )}
        </div>
      )}

      {tab === "audit" && <AuditTab clientId={id} />}

      {/* Portal link dialog */}
      <Dialog open={portalOpen} onOpenChange={setPortalOpen}>
        <DialogContent className="bg-white max-w-lg">
          <DialogHeader><DialogTitle className="font-display tracking-tight">Client portal link</DialogTitle></DialogHeader>
          <p className="text-sm text-[#57534E]">Share this private link with {client.first_name}. They can see upcoming appointments and complete their intake — no account needed.</p>
          {portalLoading ? (
            <div className="py-8 text-center text-sm text-[#57534E]">Preparing link…</div>
          ) : portalToken ? (
            <>
              <div className="flex gap-2">
                <input readOnly value={portalUrl} className="flex-1 text-xs px-3 py-2 border border-[#E8E2D9] rounded-lg bg-[#FAF8F5] font-mono" data-testid="portal-url" />
                <Button data-testid="copy-portal-link" onClick={async () => {
                  try { await navigator.clipboard.writeText(portalUrl); toast.success("Link copied"); }
                  catch (_) {
                    const ta = document.createElement("textarea");
                    ta.value = portalUrl; document.body.appendChild(ta); ta.select();
                    try { document.execCommand("copy"); toast.success("Link copied"); }
                    catch (e2) { toast.error("Could not copy — please copy manually"); }
                    finally { document.body.removeChild(ta); }
                  }
                }} variant="outline" className="rounded-full"><Copy size={13} className="mr-1" /> Copy</Button>
              </div>
              <div className="text-xs text-[#A8A29E]">Anyone with this link can view. Revoke below when it&apos;s no longer needed.</div>
            </>
          ) : null}
          <DialogFooter className="gap-2">
            {portalToken && <Button data-testid="revoke-portal-btn" variant="outline" onClick={revokePortal} className="rounded-full text-red-600 border-red-200 mr-auto"><XIcon size={13} className="mr-1" /> Revoke link</Button>}
            <Button onClick={() => setPortalOpen(false)} variant="outline" className="rounded-full">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit dialog */}
      {/* ── AI Client Summary Drawer ─────────────────────────────── */}
      <Sheet open={aiOpen} onOpenChange={(v) => { setAiOpen(v); if (!v) { setAiData(null); setAiError(null); } }}>
        <SheetContent side="right" className="w-full sm:max-w-lg overflow-y-auto bg-white" data-testid="ai-summary-drawer">
          <SheetHeader className="pr-8">
            <SheetTitle className="font-display tracking-tight text-2xl leading-tight" data-testid="ai-summary-title">AI Client Summary</SheetTitle>
            <SheetDescription>{client?.first_name} {client?.last_name}</SheetDescription>
          </SheetHeader>
          <div className="mt-5 space-y-5">
            {aiLoading && (
              <div className="text-sm text-[#57534E]" data-testid="ai-summary-loading">Generating summary…</div>
            )}
            {aiError && !aiLoading && (
              <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-xl p-3" data-testid="ai-summary-error">{aiError}</div>
            )}
            {aiData && !aiLoading && !aiError && (
              <div data-testid="ai-summary-content" className="space-y-4">
                {[
                  ["Client Overview", "client_overview"],
                  ["Current Priorities", "current_priorities"],
                  ["Open Items", "open_items"],
                  ["Suggested Next Steps", "suggested_next_steps"],
                ].map(([label, key]) => (
                  <div key={key} data-testid={`ai-section-${key}`}>
                    <div className="text-[10px] uppercase tracking-widest text-[#A8A29E] mb-1">{label}</div>
                    <div className="text-sm whitespace-pre-wrap text-[#1C1917]">{aiData.sections?.[key] || "—"}</div>
                  </div>
                ))}
                <div className="text-[11px] italic text-[#A8A29E] pt-3 border-t border-[#E8E2D9]">
                  AI-generated summary. Review the information before relying on it or taking action.
                </div>
              </div>
            )}
            <div className="pt-4 border-t border-[#E8E2D9] flex items-center gap-2 flex-wrap">
              <Button data-testid="ai-summary-regenerate-btn" onClick={generateAiSummary} disabled={aiLoading} variant="outline" className="rounded-full">
                <RotateCcw size={13} className="mr-1.5" /> Regenerate
              </Button>
              <Button data-testid="ai-summary-copy-btn" onClick={copyAiSummary} disabled={!aiData || aiLoading} variant="outline" className="rounded-full">
                <Copy size={13} className="mr-1.5" /> Copy Summary
              </Button>
              <Button data-testid="ai-summary-close-btn" onClick={() => setAiOpen(false)} variant="ghost" className="rounded-full ml-auto">
                <XIcon size={13} className="mr-1" /> Close
              </Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="bg-white max-w-2xl">
          <DialogHeader><DialogTitle className="font-display tracking-tight">Edit client</DialogTitle></DialogHeader>
          {editForm && (
            <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>First name *</Label><Input value={editForm.first_name} onChange={(e) => setEditForm({...editForm, first_name: e.target.value})} className="mt-1.5" data-testid="edit-first-name" /></div>
                <div><Label>Last name *</Label><Input value={editForm.last_name} onChange={(e) => setEditForm({...editForm, last_name: e.target.value})} className="mt-1.5" data-testid="edit-last-name" /></div>
                <div><Label>Preferred name</Label><Input value={editForm.preferred_name} onChange={(e) => setEditForm({...editForm, preferred_name: e.target.value})} className="mt-1.5" /></div>
                <div><Label>Date of birth</Label><Input type="date" value={editForm.date_of_birth} onChange={(e) => setEditForm({...editForm, date_of_birth: e.target.value})} className="mt-1.5" /></div>
                <div><Label>Phone</Label><Input value={editForm.phone} onChange={(e) => setEditForm({...editForm, phone: e.target.value})} className="mt-1.5" /></div>
                <div><Label>Email</Label><Input value={editForm.email} onChange={(e) => setEditForm({...editForm, email: e.target.value})} className="mt-1.5" /></div>
              </div>
              <div><Label>Street address</Label><Input value={editForm.street_address} onChange={(e) => setEditForm({...editForm, street_address: e.target.value})} className="mt-1.5" /></div>
              <div className="grid grid-cols-[1fr_120px_120px] gap-3">
                <div><Label>City</Label><Input value={editForm.city} onChange={(e) => setEditForm({...editForm, city: e.target.value})} className="mt-1.5" /></div>
                <div><Label>State</Label><Input value={editForm.state} onChange={(e) => setEditForm({...editForm, state: e.target.value})} className="mt-1.5" /></div>
                <div><Label>ZIP code</Label><Input value={editForm.zip_code} onChange={(e) => setEditForm({...editForm, zip_code: e.target.value})} className="mt-1.5" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Emergency contact</Label><Input value={editForm.emergency_contact_name} onChange={(e) => setEditForm({...editForm, emergency_contact_name: e.target.value})} className="mt-1.5" data-testid="edit-emerg-name" /></div>
                <div><Label>Emergency phone</Label><Input value={editForm.emergency_contact_phone} onChange={(e) => setEditForm({...editForm, emergency_contact_phone: e.target.value})} className="mt-1.5" data-testid="edit-emerg-phone" /></div>
                <div><Label>Referral source</Label><Input value={editForm.referral_source} onChange={(e) => setEditForm({...editForm, referral_source: e.target.value})} className="mt-1.5" data-testid="edit-referral" /></div>
                <div><Label>Intake date</Label><Input type="date" value={editForm.intake_date} onChange={(e) => setEditForm({...editForm, intake_date: e.target.value})} className="mt-1.5" data-testid="edit-intake-date" /></div>
              </div>
              <div><Label>Insurance information</Label><Textarea rows={2} value={editForm.insurance_info} onChange={(e) => setEditForm({...editForm, insurance_info: e.target.value})} className="mt-1.5" data-testid="edit-insurance" /></div>
              <div><Label>Status</Label>
                <Select value={editForm.status} onValueChange={(v) => setEditForm({...editForm, status: v})}>
                  <SelectTrigger className="mt-1.5" data-testid="edit-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Notes</Label><Textarea rows={3} value={editForm.notes} onChange={(e) => setEditForm({...editForm, notes: e.target.value})} className="mt-1.5" /></div>
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button onClick={() => setEditOpen(false)} variant="outline" className="rounded-full">Cancel</Button>
            <Button data-testid="save-edit-client-btn" onClick={saveEdit} className="rounded-full bg-[#E8712C] hover:bg-[#C55A1F]">Save changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, value, full = false }) {
  return (
    <div className={full ? "sm:col-span-2" : ""}>
      <dt className="text-xs text-[#A8A29E] uppercase tracking-widest">{label}</dt>
      <dd className="text-sm text-[#1C1917] mt-0.5">{value || <span className="text-[#A8A29E]">—</span>}</dd>
    </div>
  );
}

function MetaCard({ label, value }) {
  return (
    <div className="bg-white rounded-2xl border border-[#E8E2D9] p-4">
      <div className="text-xs text-[#A8A29E] uppercase tracking-widest">{label}</div>
      <div className="text-lg font-display font-semibold mt-1">{value}</div>
    </div>
  );
}

// Client Overview summary sidebar — 5 compact cards, every one clickable to
// the right specific record or filtered tab. Kept concise on purpose so it
// doesn't duplicate every other tab.
function OverviewSummary({ client, appointments, bookings, tasks, invoices, contracts, formAssignments, onNav, canEdit }) {
  const navigate = useNavigate();
  const now = new Date();
  const clientId = client.id;

  // Next appointment: prefer bookings (customer-facing), else appointments.
  const upcomingBooking = bookings.find((b) => new Date(b.starts_at) > now && b.status !== "cancelled");
  const upcomingAppt = appointments.find((a) => new Date(a.starts_at) > now && a.status !== "cancelled");
  const nextEvent = upcomingBooking
    ? { kind: "booking", id: upcomingBooking.id, when: upcomingBooking.starts_at, label: upcomingBooking.appointment_type_snapshot?.name || "Booking" }
    : upcomingAppt
      ? { kind: "appointment", id: upcomingAppt.id, when: upcomingAppt.starts_at, label: upcomingAppt.title }
      : null;

  const openTasks = tasks.filter((t) => t.status !== "completed");
  const outstandingInvoice = invoices.find((i) => ["sent", "overdue"].includes(i.status));
  const activeContract = contracts.find((c) => ["signed", "active", "sent"].includes(c.status)) || contracts[0];
  const pendingForm = formAssignments.find((fa) => fa.status !== "submitted");

  const cta = (label, testid, onClick) => canEdit ? (
    <Button
      size="sm"
      onClick={(e) => { e.stopPropagation(); onClick(); }}
      data-testid={testid}
      className="mt-2 rounded-full bg-[#E8712C] hover:bg-[#C55A1F] text-white h-9 px-4 text-xs"
    >
      <Plus size={14} className="mr-1.5" /> {label}
    </Button>
  ) : null;

  return (
    <>
      {/* Next appointment / booking */}
      <div className="w-full text-left bg-white rounded-2xl border border-[#E8E2D9] p-4">
        <div className="text-xs text-[#A8A29E] uppercase tracking-widest">Next appointment</div>
        {nextEvent ? (
          <button
            onClick={() => nextEvent.kind === "booking"
              ? navigate(`/app/scheduling?id=${nextEvent.id}`)
              : navigate(`/app/calendar?id=${nextEvent.id}`)}
            data-testid="overview-next-appt"
            className="w-full text-left mt-1 hover:text-[#E8712C] transition-colors"
          >
            <div className="text-sm font-semibold truncate">{nextEvent.label}</div>
            <div className="text-xs text-[#E8712C] mt-0.5">{formatDateTime(nextEvent.when)}</div>
          </button>
        ) : (
          <>
            <div className="text-sm text-[#57534E] mt-1">No upcoming appointments</div>
            {cta("Schedule appointment", "overview-cta-schedule",
              () => navigate(`/app/calendar?new=1&client_id=${clientId}`))}
          </>
        )}
      </div>

      {/* Open tasks */}
      <div className="w-full text-left bg-white rounded-2xl border border-[#E8E2D9] p-4">
        <div className="text-xs text-[#A8A29E] uppercase tracking-widest">Open tasks</div>
        {openTasks.length > 0 ? (
          <button
            onClick={() => onNav("tasks")}
            data-testid="overview-open-tasks"
            className="w-full text-left mt-1 hover:text-[#E8712C] transition-colors"
          >
            <div className="text-lg font-display font-semibold">{openTasks.length}</div>
            <div className="text-xs text-[#57534E] truncate mt-0.5">Next: {openTasks[0].title}</div>
          </button>
        ) : (
          <>
            <div className="text-sm text-[#57534E] mt-1">No open tasks</div>
            {cta("Create task", "overview-cta-task",
              () => navigate(`/app/tasks?new=1&client_id=${clientId}`))}
          </>
        )}
      </div>

      {/* Outstanding invoice */}
      <div className="w-full text-left bg-white rounded-2xl border border-[#E8E2D9] p-4">
        <div className="text-xs text-[#A8A29E] uppercase tracking-widest">Outstanding invoice</div>
        {outstandingInvoice ? (
          <button
            onClick={() => navigate(`/app/invoices?id=${outstandingInvoice.id}`)}
            data-testid="overview-outstanding-invoice"
            className="w-full text-left mt-1 hover:text-[#E8712C] transition-colors"
          >
            <div className="text-sm font-semibold">
              {outstandingInvoice.invoice_number} · ${((outstandingInvoice.total_cents || 0) / 100).toLocaleString(undefined, { minimumFractionDigits: 2 })}
            </div>
            <div className="text-xs text-[#57534E] mt-0.5 flex items-center gap-1.5">
              <StatusBadge value={outstandingInvoice.status} />
              {outstandingInvoice.due_date ? ` Due ${formatDate(outstandingInvoice.due_date)}` : ""}
            </div>
          </button>
        ) : (
          <>
            <div className="text-sm text-[#57534E] mt-1">Nothing outstanding</div>
            {cta("Create invoice", "overview-cta-invoice",
              () => navigate(`/app/invoices?new=1&client_id=${clientId}`))}
          </>
        )}
      </div>

      {/* Pending form */}
      <div className="w-full text-left bg-white rounded-2xl border border-[#E8E2D9] p-4">
        <div className="text-xs text-[#A8A29E] uppercase tracking-widest">Forms requiring action</div>
        {pendingForm ? (
          <button
            onClick={() => onNav("forms")}
            data-testid="overview-pending-form"
            className="w-full text-left mt-1 hover:text-[#E8712C] transition-colors"
          >
            <div className="text-sm font-semibold truncate">{pendingForm.form_templates?.name || "Form"}</div>
            <div className="text-xs text-[#57534E] mt-0.5"><StatusBadge value={pendingForm.status} /></div>
          </button>
        ) : (
          <>
            <div className="text-sm text-[#57534E] mt-1">All caught up</div>
            {cta("Send form", "overview-cta-form",
              () => navigate(`/app/forms-documents?client_id=${clientId}&kind=form`))}
          </>
        )}
      </div>

      {/* Contract */}
      <div className="w-full text-left bg-white rounded-2xl border border-[#E8E2D9] p-4">
        <div className="text-xs text-[#A8A29E] uppercase tracking-widest">Contract</div>
        {activeContract ? (
          <button
            onClick={() => navigate(`/app/contracts?id=${activeContract.id}`)}
            data-testid="overview-active-contract"
            className="w-full text-left mt-1 hover:text-[#E8712C] transition-colors"
          >
            <div className="text-sm font-semibold truncate">{activeContract.contract_name}</div>
            <div className="text-xs text-[#57534E] mt-0.5"><StatusBadge value={activeContract.status} /></div>
          </button>
        ) : (
          <>
            <div className="text-sm text-[#57534E] mt-1">No contract yet</div>
            {cta("Create contract", "overview-cta-contract",
              () => navigate(`/app/contracts?new=1&client_id=${clientId}`))}
          </>
        )}
      </div>

      <MetaCard label="Client since" value={formatDate(client.created_at)} />
    </>
  );
}

function ListPanel({ children, testid, empty }) {
  const arr = Array.isArray(children) ? children : [children].filter(Boolean);
  if (arr.length === 0) {
    const Icon = empty.icon;
    return (
      <div className="text-center py-12 bg-white rounded-2xl border border-[#E8E2D9]" data-testid={testid}>
        <Icon size={22} className="mx-auto text-[#A8A29E] mb-2" strokeWidth={1.5} />
        <p className="text-sm font-medium">{empty.title}</p>
        <p className="text-xs text-[#57534E] mt-1 mb-4">{empty.desc}</p>
        {empty.action}
      </div>
    );
  }
  return <div className="bg-white rounded-2xl border border-[#E8E2D9] overflow-hidden" data-testid={testid}>{children}</div>;
}

function IntakePanel({ intake, client, onChange }) {
  const { user, currentOrgId } = useAuth();
  const [saving, setSaving] = useState(false);
  const [local, setLocal] = useState({
    status: intake.status,
    referral_source: intake.referral_source || "",
    reason: intake.reason || "",
    notes: intake.notes || "",
    checklist: intake.checklist || {},
  });

  const pc = intake.primary_contact || {};
  const pd = intake.project_details || {};
  const sr = intake.service_request || {};
  const cn = intake.consent || {};
  const hasWizardData = (pc.first_name || pd.project_name || (sr.services || []).length > 0);

  const REQUIRED = [
    { key: "client_info", label: "Client information" },
    { key: "contact_info", label: "Contact information" },
    { key: "insurance_info", label: "Insurance information" },
    { key: "referral_info", label: "Referral information" },
    { key: "required_documents", label: "Required documents" },
    { key: "provider_info", label: "Provider information" },
    { key: "final_review", label: "Final review" },
  ];
  const progress = Math.round(
    (REQUIRED.filter((r) => local.checklist[r.key]).length / REQUIRED.length) * 100
  );
  const allDone = progress === 100;

  const toggle = (k) => setLocal((s) => ({ ...s, checklist: { ...s.checklist, [k]: !s.checklist[k] } }));

  const save = async (markComplete = false) => {
    setSaving(true);
    const payload = {
      status: markComplete ? "complete" : local.status,
      completed_at: markComplete ? new Date().toISOString() : intake.completed_at,
      referral_source: local.referral_source || null,
      reason: local.reason || null,
      notes: local.notes || null,
      checklist: local.checklist,
    };
    const { error } = await supabase.from("intake_records").update(payload).eq("id", intake.id);
    setSaving(false);
    if (error) return toast.error(error.message);
    toast.success(markComplete ? "Intake completed" : "Intake saved");
    onChange();
    logActivity({ organization_id: currentOrgId, actor_user_id: user.id, client_id: client.id,
      action: markComplete ? "intake.completed" : "intake.updated",
      summary: markComplete ? `completed intake for ${client.first_name} ${client.last_name}` : `updated intake for ${client.first_name} ${client.last_name}`,
      entity_type: "intake_records", entity_id: intake.id });
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-2xl border border-[#E8E2D9] p-6">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <div>
            <div className="text-xs text-[#57534E] uppercase tracking-widest mb-1">Intake status</div>
            <StatusBadge value={local.status} />
          </div>
          <div className="text-right">
            <div className="text-xs text-[#57534E] uppercase tracking-widest mb-1">Progress</div>
            <div className="text-2xl font-display font-bold">{progress}%</div>
          </div>
        </div>
        <div className="h-2 rounded-full bg-[#F5EDE4] overflow-hidden">
          <div className="h-full rounded-full bg-[#E8712C] transition-all" style={{ width: `${progress}%` }} />
        </div>
      </div>

      {hasWizardData && (
        <div className="bg-white rounded-2xl border border-[#E8E2D9] p-6 space-y-6" data-testid="intake-wizard-view">
          <div className="flex items-center justify-between">
            <h3 className="font-display font-semibold text-lg tracking-tight">Intake submission</h3>
            <span className="text-[10px] uppercase tracking-widest px-2 py-1 rounded-full bg-[#E8F0E4] text-[#2C3A29]">4-step wizard</span>
          </div>

          {pc.first_name && (
            <IntakeSection title="Primary Contact">
              <IntakeRow label="Name" value={`${pc.first_name || ""} ${pc.last_name || ""}`.trim()} />
              <IntakeRow label="Email" value={pc.email} />
              <IntakeRow label="Phone" value={pc.phone} />
              <IntakeRow label="Organization" value={pc.organization_name} />
              <IntakeRow label="Job title" value={pc.job_title} />
              <IntakeRow label="Preferred contact" value={pc.preferred_contact_method} />
              <IntakeRow label="Best time to contact" value={pc.best_time_to_contact} />
              <IntakeRow label="Authorized decision-maker" value={pc.decision_authority} />
            </IntakeSection>
          )}

          {pd.project_name && (
            <IntakeSection title="Project Details">
              <IntakeRow label="Project name" value={pd.project_name} />
              <IntakeRow label="Priority" value={pd.priority} />
              <IntakeRow label="Requested start" value={pd.requested_start_date && formatDate(pd.requested_start_date)} />
              <IntakeRow label="Desired completion" value={pd.desired_completion_date && formatDate(pd.desired_completion_date)} />
              <IntakeRow label="Hard deadline" value={pd.has_deadline ? (pd.deadline_date ? formatDate(pd.deadline_date) : "Yes") : "No"} />
              <IntakeRow label="Brief description" value={pd.brief_description} wide />
              <IntakeRow label="Main goal" value={pd.main_goal} wide />
              <IntakeRow label="Desired outcome" value={pd.desired_outcome} wide />
              <IntakeRow label="Additional notes" value={pd.additional_notes} wide />
            </IntakeSection>
          )}

          {(sr.services || []).length > 0 && (
            <IntakeSection title="Service Requested">
              <div className="md:col-span-2">
                <div className="text-[10px] uppercase tracking-widest text-[#A8A29E] mb-1.5">Services</div>
                <div className="flex flex-wrap gap-1.5">
                  {sr.services.map((s) => (
                    <span key={s} className="px-2 py-0.5 rounded-full bg-[#FDE9D8] text-[#C55A1F] text-xs">{s}</span>
                  ))}
                </div>
              </div>
              <IntakeRow label='"Other" description' value={sr.other_description} wide />
              <IntakeRow label="Details" value={sr.details} wide />
            </IntakeSection>
          )}

          {cn && cn.agreed !== undefined && (
            <IntakeSection title="Consent & Signature">
              <IntakeRow label="Info is accurate" value={cn.info_accurate ? "Yes" : "No"} />
              <IntakeRow label="Authorized contact" value={cn.authorized_contact ? "Yes" : "No"} />
              <IntakeRow label="No-guarantee acknowledged" value={cn.no_guarantee_acknowledged ? "Yes" : "No"} />
              <IntakeRow label="Preferred communication" value={(cn.preferred_communication || []).join(", ") || "—"} />
              <IntakeRow label="Electronic signature" value={cn.signature_full_name} />
              <IntakeRow label="Signed at" value={cn.signed_at && formatDateTime(cn.signed_at)} />
            </IntakeSection>
          )}
        </div>
      )}

      <div className="bg-white rounded-2xl border border-[#E8E2D9] p-6">
        <h3 className="font-display font-semibold text-sm text-[#57534E] uppercase tracking-widest mb-4">Intake checklist</h3>
        <div className="space-y-2">
          {REQUIRED.map((r) => (
            <label key={r.key} className="flex items-center gap-3 py-2 cursor-pointer">
              <input type="checkbox" checked={!!local.checklist[r.key]} onChange={() => toggle(r.key)}
                data-testid={`chk-${r.key}`} className="w-4 h-4 accent-[#E8712C]" />
              <span className={`text-sm ${local.checklist[r.key] ? "line-through text-[#A8A29E]" : "text-[#1C1917]"}`}>{r.label}</span>
            </label>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label>Referral source</Label>
          <Input data-testid="intake-referral" value={local.referral_source} onChange={(e) => setLocal({...local, referral_source: e.target.value})} className="mt-1.5" placeholder="e.g. Dr. Smith, insurance provider" />
        </div>
        <div>
          <Label>Status</Label>
          <Select value={local.status} onValueChange={(v) => setLocal({...local, status: v})}>
            <SelectTrigger className="mt-1.5" data-testid="intake-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="not_started">Not started</SelectItem>
              <SelectItem value="in_progress">In progress</SelectItem>
              <SelectItem value="waiting_for_information">Waiting for information</SelectItem>
              <SelectItem value="complete">Complete</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <div>
        <Label>Reason for intake</Label>
        <Textarea data-testid="intake-reason" rows={2} value={local.reason} onChange={(e) => setLocal({...local, reason: e.target.value})} className="mt-1.5" />
      </div>
      <div>
        <Label>Intake notes</Label>
        <Textarea data-testid="intake-notes" rows={4} value={local.notes} onChange={(e) => setLocal({...local, notes: e.target.value})} className="mt-1.5" />
      </div>

      <div className="flex flex-wrap justify-end gap-2">
        <Button data-testid="save-intake-btn" variant="outline" className="rounded-full" onClick={() => save(false)} disabled={saving}>Save progress</Button>
        <Button data-testid="complete-intake-btn" onClick={() => save(true)} disabled={saving || !allDone}
          className="rounded-full bg-[#E8712C] hover:bg-[#C55A1F] disabled:opacity-50">
          {allDone ? "Mark intake complete" : `Complete checklist to finish (${progress}%)`}
        </Button>
      </div>
    </div>
  );
}

function IntakeSection({ title, children }) {
  return (
    <div>
      <h4 className="text-xs uppercase tracking-widest text-[#57534E] mb-3 pb-2 border-b border-[#E8E2D9]">{title}</h4>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3">{children}</div>
    </div>
  );
}

function IntakeRow({ label, value, wide }) {
  if (!value && value !== 0 && value !== false) return null;
  return (
    <div className={wide ? "md:col-span-2" : ""}>
      <div className="text-[10px] uppercase tracking-widest text-[#A8A29E]">{label}</div>
      <div className="text-sm text-[#1C1917] whitespace-pre-wrap">{String(value)}</div>
    </div>
  );
}
